/**
 * Единица работы на проводе (P3 ревью 2026-10-18; таблица — `migrations/0023_work_items.sql`,
 * политика — `services/backend/src/domain/work-items.ts`, словарь отказов — `../failure.ts`).
 *
 * Две формы:
 *   · `WorkFailureBodySchema` — что СЕРВИС сообщает, отпуская работу с отказом: вид из закрытого
 *     словаря, деталь и подсказку провайдера. Политику (повторить, когда, или закрыть) сервис не
 *     выбирает — её выбирает бэкенд по виду;
 *   · `WorkItemViewSchema` — что видит ЧЕЛОВЕК: номер попытки, когда следующая, почему снято.
 *     По ней полоса состояния пишет «попытка N, следующая в HH:MM» и «снята: причина».
 */

import * as z from 'zod';
import { WORK_FAILURE_KINDS } from '../failure.ts';
import { ISO_UTC_MS } from '../time.ts';
import { codePointLen } from '../units.ts';

const IsoUtcMs = z.string().regex(ISO_UTC_MS);

export const WORK_KINDS = ['topic', 'generation_request', 'monitoring_request', 'rework'] as const;
export type WorkKind = (typeof WORK_KINDS)[number];

export const WORK_STATES = ['queued', 'taken', 'done', 'failed', 'cancelled'] as const;
export const WorkStateSchema = z.enum(WORK_STATES);
export type WorkState = z.output<typeof WorkStateSchema>;

/**
 * Почему работа закрыта не успехом. Зеркало CHECK `work_item.terminal_reason` (0023): слово
 * здесь и слово в DDL расходиться не имеют права — тест `work-items.test.ts` бэкенда читает оба.
 */
export const WORK_TERMINAL_REASONS = [
  /** Временные отказы исчерпали попытки политики. */
  'attempts_exhausted',
  /** Отказ, повтор которого дал бы то же самое за те же деньги. */
  'deterministic_failure',
  /** Модель отказалась (`stop_reason: refusal`). */
  'model_refusal',
  /** Стоп аккаунта или бренда (квота, ключ, биллинг) у работы, которую ждёт человек. */
  'budget_stop',
  /** Отменил человек. */
  'cancelled_by_human',
  /** Закрыт корень родословной: заявка, ради которой работа шла, отменена или кончилась. */
  'lineage_closed',
  /** Черновик удалён — переделывать нечего. */
  'draft_discarded',
  /** Свободная тема простояла дольше срока своего формата. */
  'expired',
  /** Ждущий сдался: писатель не дождался мониторинга, и прогон больше никому не нужен. */
  'abandoned',
  /** Исполнитель пропал: ни сигнала жизни, ни исхода дольше окна молчания. */
  'stale',
] as const;
export const WorkTerminalReasonSchema = z.enum(WORK_TERMINAL_REASONS);
export type WorkTerminalReason = z.output<typeof WorkTerminalReasonSchema>;

export const WORK_FAILURE_DETAIL_MAX = 500;

/**
 * Отказ, с которым сервис отпускает работу. `retry_after_ms` — подсказка провайдера (заголовок
 * `retry-after` 429): бэкенд не повторит раньше неё, но и не обязан ждать дольше своей паузы.
 */
export const WorkFailureBodySchema = z.strictObject({
  kind: z.enum(WORK_FAILURE_KINDS),
  detail: z
    .string()
    .min(1)
    .refine((s) => codePointLen(s) <= WORK_FAILURE_DETAIL_MAX, {
      error: `деталь длиннее ${String(WORK_FAILURE_DETAIL_MAX)} кодпоинтов`,
    })
    .nullable()
    .default(null),
  retry_after_ms: z.int().min(0).max(86_400_000).nullable().default(null),
});
export type WorkFailureBody = z.output<typeof WorkFailureBodySchema>;
export type WorkFailureInput = z.input<typeof WorkFailureBodySchema>;

/** Состояние работы глазами человека. */
export const WorkItemViewSchema = z.strictObject({
  state: WorkStateSchema,
  /** Сколько попыток НАЧАТО (взятий). 0 — ещё ни одной. */
  attempt: z.int().min(0),
  max_attempts: z.int().min(1),
  /** Не раньше этого момента работа будет взята снова; `null` — ограничения нет. */
  next_attempt_at: IsoUtcMs.nullable(),
  deadline_at: IsoUtcMs.nullable(),
  terminal_reason: WorkTerminalReasonSchema.nullable(),
  last_failure_kind: z.string().nullable(),
  last_failure_detail: z.string().nullable(),
});
export type WorkItemView = z.output<typeof WorkItemViewSchema>;

/** Ответ маршрута отказа переделки (`POST /intake/v1/rework-requests/:id/failure`). */
export const WorkFailureOutcomeSchema = z.strictObject({
  work: WorkItemViewSchema,
});
export type WorkFailureOutcome = z.output<typeof WorkFailureOutcomeSchema>;
